import { Button, Drawer, RangeSlider } from "@wrstudios/components";
import { IconPencil } from "@wrstudios/icons";
import { formatCurrencyShort } from "@wrstudios/utils";
import React, { useEffect, useState } from "react";
import Helmet from "react-helmet";
import { useTheme } from "styled-components";
import {
  getAveragePrice,
  getAveragePricePerSqft
} from "../../../utils/listings";
import { stripNumber } from "../../../utils/number";
import { formatCurrentInputValue } from "../../../utils/string";
import SubjectPropertyPin from "../../common/SubjectPropertyPin";
import SuggestedListPriceEdit from "../../suggested-list-price/SuggestedListPriceEdit";
import AgentNote from "../common/AgentNote";
import LiveHeader from "../common/LiveHeader";
import { useLive } from "../LiveProvider";
import {
  Address,
  Container,
  DrawerWrapper,
  EditIcon,
  EditNetSheet,
  Estimate,
  EstimateCalculations,
  EstimatedColumnOne,
  EstimatedColumnThree,
  EstimatedColumnTwo,
  EstimateDisclaimer,
  EstimateRow,
  EstimateTitle,
  HeaderContainer,
  HeaderEdit,
  HeaderWrapper,
  MapStatic,
  RangeSliderWrapper,
  Stat,
  StatImage,
  StatImageContainer,
  StatLabel,
  StatPercentage,
  Stats,
  StatsCell,
  StatsFilter,
  StatsFilters,
  StatsGrid,
  StatsGroup,
  StatsTitle,
  StatValue,
  Title,
  Total
} from "./styled/live-estimated-market-value";

export function getFormattedPriceRange(subjectProperty) {
  const range = [
    subjectProperty.priceRange.min,
    subjectProperty.priceRange.max
  ].filter(Boolean);
  if (!range.length) return "";
  return range
    .map((value) => formatCurrentInputValue(value))
    .join(` ${subjectProperty.priceRange.sep} `);
}

function getFormattedAveragePrice(listingsFilter, averageValues) {
  switch (listingsFilter) {
    case "sold":
      return averageValues.sold.averagePrice
        ? formatCurrencyShort(averageValues.sold.averagePrice)
        : "N/A";
    case "active":
      return averageValues.active.averagePrice
        ? formatCurrencyShort(averageValues.active.averagePrice)
        : "N/A";
    default:
      return averageValues.all.averagePrice
        ? formatCurrencyShort(averageValues.all.averagePrice)
        : "N/A";
  }
}

function getAveragePriceForPercentage(listingsFilter, averageValues) {
  switch (listingsFilter) {
    case "sold":
      return averageValues.sold.averagePrice
        ? averageValues.sold.averagePrice
        : "N/A";
    case "active":
      return averageValues.active.averagePrice
        ? averageValues.active.averagePrice
        : "N/A";
    default:
      return averageValues.all.averagePrice
        ? averageValues.all.averagePrice
        : "N/A";
  }
}

function getAverageSqftForPercentage(listingsFilter, averageValues) {
  switch (listingsFilter) {
    case "sold":
      return averageValues.sold.averageSqft
        ? averageValues.sold.averageSqft
        : "N/A";
    case "active":
      return averageValues.active.averageSqft
        ? averageValues.active.averageSqft
        : "N/A";
    default:
      return averageValues.all.averageSqft
        ? averageValues.all.averageSqft
        : "N/A";
  }
}

function getFormattedAverageSqft(listingsFilter, averageValues) {
  switch (listingsFilter) {
    case "sold":
      return averageValues.sold.averageSqft
        ? formatCurrentInputValue(Math.round(averageValues.sold.averageSqft))
        : "N/A";
    case "active":
      return averageValues.active.averageSqft
        ? formatCurrentInputValue(Math.round(averageValues.active.averageSqft))
        : "N/A";
    default:
      return averageValues.all.averageSqft
        ? formatCurrentInputValue(Math.round(averageValues.all.averageSqft))
        : "N/A";
  }
}

function LiveEstimateMarketValue() {
  const theme = useTheme();
  const {
    report,
    subjectProperty,
    setSubjectProperty,
    visibleListings,
    isReportOwnedByCurrentAgent
  } = useLive();

  const [estimatedSalesPrice, setEstimatedSalesPrice] = useState(250);

  useEffect(() => {
    subjectProperty.priceRange.max > subjectProperty.priceRange.min
      ? setEstimatedSalesPrice(
          (subjectProperty.priceRange.max + subjectProperty.priceRange.min) / 2
        )
      : setEstimatedSalesPrice(subjectProperty.priceRange.min);
  }, [subjectProperty]);
  const [netProceeds, setNetProceeds] = useState(0);
  useEffect(() => {
    var netSheetTotal = 0;
    subjectProperty.netSheetItems.map((netSheetItem) => {
      netSheetItem.percentage
        ? (netSheetTotal += Math.round(
            (stripNumber(estimatedSalesPrice) * netSheetItem.percentage) / 100
          ))
        : (netSheetTotal += Math.round(netSheetItem.value));
    });
    setNetProceeds(stripNumber(estimatedSalesPrice) - netSheetTotal);
  }, [estimatedSalesPrice, subjectProperty]);

  const formattedPriceRange = getFormattedPriceRange(subjectProperty);
  const [listingsFilter, setListingsFilter] = useState("all");
  const [averageValues, setAverageValues] = useState({
    all: {
      averagePrice: getAveragePrice(visibleListings),
      averageSqft: getAveragePricePerSqft(visibleListings)
    },
    sold: {},
    active: {}
  });

  const [showDrawer, setShowDrawer] = useState(false);
  const [isRange, setIsRange] = useState(false);

  useEffect(() => {
    const listings = visibleListings.reduce(
      (filteredListings, listing) => {
        if (listing.soldPrice) {
          filteredListings.sold.push(listing);
        } else {
          filteredListings.active.push(listing);
        }
        return filteredListings;
      },
      {
        sold: [],
        active: []
      }
    );

    setAverageValues((prevValues) => ({
      ...prevValues,
      sold: {
        averagePrice: getAveragePrice(listings.sold),
        averageSqft: getAveragePricePerSqft(listings.sold)
      },
      active: {
        averagePrice: getAveragePrice(listings.active),
        averageSqft: getAveragePricePerSqft(listings.active)
      }
    }));
  }, []);

  useEffect(() => {
    setIsRange(subjectProperty.priceRange.max > 0 ? true : false);
  }, [subjectProperty]);

  return (
    <>
      <Helmet>
        <title>Suggested List Price</title>
      </Helmet>
      <LiveHeader>Suggested List Price</LiveHeader>
      <HeaderContainer>
        <HeaderWrapper>
          <AgentNote anchor="top" note={subjectProperty.note} />
          <Address className="page-address">{subjectProperty.address}</Address>
          <Title className="page-title-price-range">
            {formattedPriceRange || "$---"}
          </Title>
        </HeaderWrapper>
        <HeaderEdit>
          {isReportOwnedByCurrentAgent && (
            <Button
              style={{ borderColor: "#3E83c1", color: "#3E83c1" }}
              variant="secondary"
              onClick={() => setShowDrawer(true)}>
              <EditIcon>
                <IconPencil />
              </EditIcon>
              Edit
            </Button>
          )}
        </HeaderEdit>
      </HeaderContainer>
      <Container>
        <Stats>
          <StatsGrid>
            <StatsCell>
              <StatImageContainer>
                {!subjectProperty.photo &&
                  !!subjectProperty.coords.lat &&
                  subjectProperty.coords.lon && (
                    <MapStatic
                      width={349}
                      height={304}
                      lat={subjectProperty.coords.lat}
                      lon={subjectProperty.coords.lon}
                      pin={
                        <SubjectPropertyPin color={theme.live.colors.primary} />
                      }
                    />
                  )}
                {!!subjectProperty.photo && (
                  <StatImage
                    src={subjectProperty.photo}
                    alt={subjectProperty.address}
                    data-error="Error loading image"
                  />
                )}
              </StatImageContainer>
            </StatsCell>
            <StatsCell>
              <div style={{ width: "100%" }}>
                <StatsTitle>Compare pricing with the comps</StatsTitle>
                <br></br>
                <br></br>
                {isRange && isReportOwnedByCurrentAgent && (
                  <RangeSliderWrapper>
                    <RangeSlider
                      style={{ width: "100%" }}
                      min={subjectProperty.priceRange.min}
                      max={subjectProperty.priceRange.max}
                      minLabel={formatCurrencyShort(
                        subjectProperty.priceRange.min
                      )}
                      maxLabel={formatCurrencyShort(
                        subjectProperty.priceRange.max
                      )}
                      value={formatCurrentInputValue(
                        Math.round(estimatedSalesPrice)
                      )}
                      onChange={(e) => setEstimatedSalesPrice(e.target.value)}
                    />
                  </RangeSliderWrapper>
                )}
                <StatsFilters className="live-estimated-market-value-filters">
                  <StatsFilter
                    className={`live-estimated-market-value-filter live-estimated-market-value-filter-all ${
                      listingsFilter === "all"
                        ? "live-estimated-market-value-filter-activated"
                        : ""
                    }`}
                    isActive={listingsFilter === "all"}
                    onClick={() => setListingsFilter("all")}>
                    All
                  </StatsFilter>
                  <StatsFilter
                    className={`live-estimated-market-value-filter live-estimated-market-value-filter-sold ${
                      listingsFilter === "sold"
                        ? "live-estimated-market-value-filter-activated"
                        : ""
                    }`}
                    isActive={listingsFilter === "sold"}
                    onClick={() => setListingsFilter("sold")}>
                    Sold
                  </StatsFilter>
                  <StatsFilter
                    className={`live-estimated-market-value-filter live-estimated-market-value-filter-active ${
                      listingsFilter === "active"
                        ? "live-estimated-market-value-filter-activated"
                        : ""
                    }`}
                    isActive={listingsFilter === "active"}
                    onClick={() => setListingsFilter("active")}>
                    Active
                  </StatsFilter>
                </StatsFilters>
                <StatsGroup>
                  <Stat>
                    <StatLabel className="estimated-market-value-stat-label">
                      Average Price
                    </StatLabel>
                    <StatValue className="estimated-market-value-stat-value">
                      {estimatedSalesPrice
                        ? getFormattedAveragePrice(
                            listingsFilter,
                            averageValues
                          )
                        : "-"}
                    </StatValue>
                    {estimatedSalesPrice &&
                      (getAveragePriceForPercentage(
                        listingsFilter,
                        averageValues
                      ) > stripNumber(estimatedSalesPrice) ? (
                        <StatPercentage
                          style={{ color: theme.styleGuide.colors.red[600] }}>
                          ↓
                          {(
                            ((getAveragePriceForPercentage(
                              listingsFilter,
                              averageValues
                            ) -
                              stripNumber(estimatedSalesPrice)) /
                              getAveragePriceForPercentage(
                                listingsFilter,
                                averageValues
                              )) *
                            100
                          ).toFixed(0)}
                          % below
                        </StatPercentage>
                      ) : (
                        <StatPercentage
                          style={{ color: theme.styleGuide.colors.green[700] }}>
                          ↑
                          {(
                            ((stripNumber(estimatedSalesPrice) -
                              getAveragePriceForPercentage(
                                listingsFilter,
                                averageValues
                              )) /
                              getAveragePriceForPercentage(
                                listingsFilter,
                                averageValues
                              )) *
                            100
                          ).toFixed(0)}
                          % above
                        </StatPercentage>
                      ))}
                  </Stat>
                  <Stat>
                    <StatLabel className="estimated-market-value-stat-label">
                      Average $/sq. ft.
                    </StatLabel>
                    <StatValue className="estimated-market-value-stat-value">
                      {estimatedSalesPrice
                        ? getFormattedAverageSqft(listingsFilter, averageValues)
                        : "-"}
                    </StatValue>
                    {estimatedSalesPrice &&
                      (subjectProperty.sqft === 0 || null ? (
                        <StatPercentage>-</StatPercentage>
                      ) : getAverageSqftForPercentage(
                          listingsFilter,
                          averageValues
                        ) >
                        stripNumber(estimatedSalesPrice) /
                          subjectProperty.sqft ? (
                        <StatPercentage
                          style={{ color: theme.styleGuide.colors.red[600] }}>
                          ↓
                          {(
                            ((getAverageSqftForPercentage(
                              listingsFilter,
                              averageValues
                            ) -
                              stripNumber(estimatedSalesPrice) /
                                subjectProperty.sqft) /
                              getAverageSqftForPercentage(
                                listingsFilter,
                                averageValues
                              )) *
                            100
                          ).toFixed(0)}
                          % below
                        </StatPercentage>
                      ) : (
                        <StatPercentage
                          style={{ color: theme.styleGuide.colors.green[700] }}>
                          ↑
                          {(
                            ((stripNumber(estimatedSalesPrice) /
                              subjectProperty.sqft -
                              getAverageSqftForPercentage(
                                listingsFilter,
                                averageValues
                              )) /
                              getAverageSqftForPercentage(
                                listingsFilter,
                                averageValues
                              )) *
                            100
                          ).toFixed(0)}
                          % above
                        </StatPercentage>
                      ))}
                  </Stat>
                </StatsGroup>
              </div>
            </StatsCell>
          </StatsGrid>
        </Stats>
        {!!subjectProperty.netSheetItems.length && estimatedSalesPrice && (
          <Estimate>
            <EstimateTitle>
              Net Sheet
              {isReportOwnedByCurrentAgent && (
                <EditNetSheet onClick={() => setShowDrawer(true)}>
                  Edit
                </EditNetSheet>
              )}
            </EstimateTitle>
            <EstimateCalculations>
              <EstimateRow>
                <span>Estimated Sales Price</span>
                <span>
                  {formatCurrentInputValue(Math.round(estimatedSalesPrice))}
                </span>
              </EstimateRow>
              {subjectProperty.netSheetItems.map((netSheetItem) => (
                <EstimateRow key={netSheetItem.id}>
                  <EstimatedColumnOne>{netSheetItem.key}</EstimatedColumnOne>
                  <EstimatedColumnTwo>
                    {netSheetItem.percentage
                      ? `${netSheetItem.percentage}%`
                      : ""}
                  </EstimatedColumnTwo>
                  <EstimatedColumnThree>
                    {netSheetItem.percentage
                      ? `${formatCurrentInputValue(
                          Math.round(
                            (stripNumber(estimatedSalesPrice) *
                              netSheetItem.percentage) /
                              100
                          )
                        )}`
                      : netSheetItem.value
                      ? `${formatCurrentInputValue(
                          Math.round(netSheetItem.value)
                        )}`
                      : "-"}
                  </EstimatedColumnThree>
                </EstimateRow>
              ))}
              <EstimateRow>
                <Total className="estimated-market-value-stat-value">
                  Estimated Proceeds
                </Total>
                <Total className="estimated-market-value-stat-value">
                  {formatCurrentInputValue(Math.round(netProceeds))}
                </Total>
              </EstimateRow>
            </EstimateCalculations>
            <EstimateDisclaimer>All values are estimates</EstimateDisclaimer>
          </Estimate>
        )}
        {showDrawer && (
          <Drawer
            direction="right"
            semiTransparentBackground={false}
            onClose={() => {
              setShowDrawer(false);
            }}>
            <DrawerWrapper>
              <SuggestedListPriceEdit
                report={report}
                subjectProperty={subjectProperty}
                setSubjectProperty={setSubjectProperty}
                onClose={() => setShowDrawer(false)}
              />
            </DrawerWrapper>
          </Drawer>
        )}
      </Container>
    </>
  );
}

export default LiveEstimateMarketValue;
